import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Renders the products the visitor has viewed in this session (excluding the current PDP product),
 * reusing the theme's native RecentlyViewed tracker and the Section Rendering API.
 */
class MoxRecentlyViewed extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  #loadRecentlyViewed() {
    const { searchUrl, sectionId, currentProductId, maxProducts } = this.dataset;
    const id = this.id;

    if (!searchUrl || !sectionId || !id) return;

    const viewedProductIds = RecentlyViewed.getProducts().filter((productId) => productId !== currentProductId);

    if (viewedProductIds.length === 0) {
      this.remove();
      return;
    }

    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', viewedProductIds.map((productId) => `id:${productId}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    if (maxProducts) url.searchParams.set('limit', maxProducts);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    sectionRenderer
      .getSectionHTML(sectionId, !Shopify.designMode, url)
      .then((html) => {
        if (this.#activeFetch?.signal.aborted) return;

        const parsed = document.createElement('div');
        parsed.innerHTML = html;
        const rendered = parsed.querySelector(`mox-recently-viewed[id="${id}"]`);
        const list = rendered?.querySelector('[data-testid="mox-recently-viewed-list"]');

        if (!rendered || !list || list.children.length === 0) {
          this.#handleEmpty();
          return;
        }

        this.innerHTML = rendered.innerHTML;
        this.removeAttribute('hidden');
      })
      .catch((error) => {
        if (this.#activeFetch?.signal.aborted) return;
        console.error('Recently viewed products error:', error.message);
        this.#handleEmpty();
      })
      .finally(() => {
        this.#activeFetch = null;
      });
  }

  #handleEmpty() {
    if (Shopify.designMode) return;
    this.remove();
  }
}

if (!customElements.get('mox-recently-viewed')) {
  customElements.define('mox-recently-viewed', MoxRecentlyViewed);
}
